
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { Article } from './types';

export const useJournalistArticles = (journalistId: string | undefined) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  useEffect(() => {
    const fetchArticles = async () => {
      if (!journalistId) {
        setArticles([]);
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch the journalist's articles
        const { data: articlesData, error: articlesError } = await supabase
          .from('articles')
          .select(`
            id, 
            title, 
            excerpt, 
            lead,
            published_at,
            reading_time,
            featured_image_url,
            header_image_url,
            payment_type,
            status,
            author_id
          `)
          .eq('author_id', journalistId)
          .eq('status', 'published')
          .order('published_at', { ascending: false });
          
        if (articlesError) throw articlesError;
        
        // Get article tags
        const articleIds = articlesData.map(article => article.id);
        
        if (articleIds.length > 0) {
          const { data: tagsData } = await supabase
            .from('article_tags')
            .select(`
              article_id,
              tags(name)
            `)
            .in('article_id', articleIds);
            
          // Map tags to articles
          const articleTags: {[key: string]: string[]} = {};
          tagsData?.forEach(tag => {
            if (!articleTags[tag.article_id]) {
              articleTags[tag.article_id] = [];
            }
            
            if (tag.tags && tag.tags.name) {
              articleTags[tag.article_id].push(tag.tags.name);
            }
          });
          
          // Format articles with their tags
          const formattedArticles = articlesData.map(article => {
            // Default Unsplash image with a stable ID to prevent random failures
            const defaultImage = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=500&h=350&auto=format&fit=crop';
            
            return {
              id: article.id,
              title: article.title,
              excerpt: article.excerpt || 'No excerpt available.',
              lead: article.lead || '',
              date: new Date(article.published_at).toISOString().split('T')[0],
              readTime: article.reading_time || Math.floor(5 + Math.random() * 10),
              category: articleTags[article.id]?.[0] || 'General',
              featured: Math.random() > 0.7, // Random for now, could be based on views later
              premium: article.payment_type !== 'free',
              image: article.featured_image_url || article.header_image_url || defaultImage,
              author_id: article.author_id
            };
          });
          
          setArticles(formattedArticles);
        } else {
          setArticles([]);
        }
      } catch (err) {
        console.error('Error fetching articles:', err);
        setError(err instanceof Error ? err : new Error('Failed to load articles'));
        toast({
          variant: 'destructive',
          title: 'Error loading articles',
          description: 'There was a problem loading the journalist\'s articles.'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchArticles();
  }, [journalistId]);
  
  return { articles, isLoading, error };
};
